/** Proactive renewal outreach — scan + due list. */
import { useEffect, useState } from 'react';
import {
  DataTable,
  FilterBar,
  GlassButton,
  GlassCard,
  GlassInput,
  StatusPill,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile } from '../components/elevated/kit.js';

interface RenewalRow {
  sourceId: string;
  reference?: string;
  sourceReference?: string;
  serviceSlug?: string;
  expiry?: string;
  customerName?: string;
  channel?: string;
  consent?: boolean;
  templateStatus?: string;
}

export function ProactivePage() {
  const { l } = useI18n();
  const [withinDays, setWithinDays] = useState(45);
  const [due, setDue] = useState<RenewalRow[]>([]);
  const [scanResult, setScanResult] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const loadDue = () => {
    admin()
      .proactiveDue(withinDays)
      .then((r) => setDue(r.due as unknown as RenewalRow[]))
      .catch(() => setError(true));
  };

  const columns: DataTableColumn<RenewalRow>[] = [
    {
      id: 'reference',
      header: l(S.reference),
      sortable: true,
      value: (row) => row.reference ?? row.sourceReference,
      render: (row) => <span className="font-mono text-xs font-bold">{row.reference ?? row.sourceReference ?? '—'}</span>,
    },
    {
      id: 'service',
      header: l(S.service),
      sortable: true,
      value: (row) => row.serviceSlug,
      render: (row) => <span className="font-semibold">{row.serviceSlug ?? 'land-transport-permit-renewal'}</span>,
    },
    {
      id: 'expiry',
      header: l({ en: 'Expiry', ar: 'الانتهاء' }),
      sortable: true,
      value: (row) => row.expiry,
      render: (row) => <span className="font-mono text-xs">{row.expiry ?? '—'}</span>,
    },
    {
      id: 'channel',
      header: l(S.channel),
      sortable: true,
      value: (row) => row.channel,
      render: (row) => <span className="rounded bg-aeblack-50 px-2 py-1 text-[10px] font-bold uppercase">{row.channel ?? 'whatsapp'}</span>,
    },
    {
      id: 'consent',
      header: l({ en: 'Consent', ar: 'الموافقة' }),
      sortable: true,
      value: (row) => row.consent ? 'yes' : 'pending',
      render: (row) => (
        <StatusPill
          state={row.consent === false ? 'rejected' : row.consent === true ? 'published' : 'draft'}
          label={
            row.consent === false
              ? l({ en: 'Missing', ar: 'مفقودة' })
              : row.consent === true
                ? l({ en: 'Eligible', ar: 'مؤهل' })
                : l({ en: 'Checked at send', ar: 'يُتحقق عند الإرسال' })
          }
        />
      ),
    },
    {
      id: 'template',
      header: l({ en: 'Template', ar: 'القالب' }),
      render: (row) => <StatusPill state={row.templateStatus === 'configured' ? 'published' : 'in_review'} label={(row.templateStatus ?? 'not_configured').replaceAll('_', ' ')} />,
    },
  ];

  useEffect(loadDue, [withinDays]);

  const scan = async () => {
    setBusy(true);
    setScanResult(null);
    try {
      const r = await admin().proactiveScan(withinDays);
      setScanResult(r.offered);
      loadDue();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const consentReady = due.filter((row) => row.consent === true).length;

  return (
    <div>
      <HeroBand
        accent="#0173ab"
        eyebrow={l(S.proactive)}
        title={l(S.proactiveTitle)}
        subtitle={l({ en: 'Consent-aware renewal outreach using approved bilingual channel templates.', ar: 'تواصل لتجديد الخدمات يراعي الموافقة ويستخدم قوالب قنوات ثنائية اللغة ومعتمدة.' })}
        metrics={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              dark
              label={l(S.proactiveDue)}
              value={due.length}
              accent={due.length ? '#d67907' : '#3f8e50'}
            />
            <MetricTile
              dark
              label={l(S.proactiveOffered)}
              value={scanResult ?? '—'}
              hint={l({ en: 'From the latest scan', ar: 'من أحدث فحص' })}
              accent="#3f8e50"
            />
            <MetricTile
              dark
              label={l({ en: 'Consent-ready', ar: 'مؤهلة بالموافقة' })}
              value={consentReady}
              hint={l({ en: 'Eligible to message now', ar: 'مؤهلة للمراسلة الآن' })}
              accent="#0173ab"
            />
            <MetricTile
              dark
              label={l({ en: 'Scan window', ar: 'نافذة الفحص' })}
              value={`${withinDays}d`}
              hint={l(S.withinDays)}
            />
          </div>
        }
      />
      <FilterBar>
        <label className="text-xs font-semibold text-ink-soft">
          {l(S.withinDays)}
          <GlassInput
            type="number"
            min={7}
            max={180}
            value={withinDays}
            onChange={(e) => setWithinDays(Number(e.target.value) || 45)}
            className="mt-1 w-28"
          />
        </label>
        <GlassButton variant="primary" disabled={busy} onClick={() => void scan()}>
          {busy ? l({ en: 'Scanning…', ar: 'جارٍ الفحص…' }) : l(S.proactiveScan)}
        </GlassButton>
      </FilterBar>
      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>}
      <GlassCard className="p-5" strong>
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-gold-deep">{l(S.proactiveDue)}</h2>
        <DataTable
          rows={due}
          columns={columns}
          rowKey={(row) => row.sourceId}
          emptyTitle={l(S.proactiveEmpty)}
          filename="renewal-outreach.csv"
        />
      </GlassCard>
    </div>
  );
}
